import { Icon } from '@gitbook/spine-icons';
import { type EmojiDef, emojis } from '@gitbook/util-emojis';

import { Emoji } from '../Emoji';
import {
    GenericPicker,
    type GenericPickerCategory,
    type GenericPickerRenderInfo,
} from './GenericPicker';

export interface EmojiPickerProps {
    /**
     * Callback when an emoji is selected.
     */
    onPick: (icon: string) => void;

    /**
     * Optional query to filter the emojis.
     * If a query is passed, a callback onChangeQuery should be passed to update the query.
     */
    query?: string;

    /**
     * Callback when the query changes.
     */
    onChangeQuery?: (query: string) => void;
}

function renderItem(emoji: EmojiDef): GenericPickerRenderInfo {
    return {
        label: emoji.name,
        icon: <Emoji code={emoji.code} />,
    };
}

function getKey(emoji: EmojiDef) {
    return emoji.code;
}

function getCategory(emoji: EmojiDef): string {
    return emoji.category;
}

const textKeys = [
    {
        name: 'name',
        weight: 0.7,
    },
    {
        name: 'keywords',
        weight: 0.3,
    },
];

const categories: GenericPickerCategory[] = [
    {
        key: 'people',
        label: 'People',
        icon: Icon.EmojiReaction,
    },
    {
        key: 'nature',
        label: 'Nature',
        icon: Icon.RoleEditor,
    },
    {
        key: 'food',
        label: 'Food',
        icon: Icon.Food,
    },
    {
        key: 'activity',
        label: 'Activity',
        icon: Icon.Music,
    },
    {
        key: 'travel',
        label: 'Travel',
        icon: Icon.EmojiBike,
    },
    {
        key: 'objects',
        label: 'Objects',
        icon: Icon.EmojiWatch,
    },
    {
        key: 'symbols',
        label: 'Symbols',
        icon: Icon.HeartOutlined,
    },
    {
        key: 'flags',
        label: 'Flags',
        icon: Icon.EmojiFlag,
    },
];

/**
 * Component to pick an emoji from a list of emojis.
 */
export function EmojiPicker(props: EmojiPickerProps) {
    return (
        <GenericPicker<EmojiDef>
            listId="emojis"
            title="Emojis"
            items={emojis}
            categories={categories}
            query={props.query}
            onChangeQuery={props.onChangeQuery}
            getKey={getKey}
            getCategory={getCategory}
            onPick={(icon) => props.onPick(icon.code)}
            renderItem={renderItem}
            textKeys={textKeys}
        />
    );
}
